import { useCallback, useRef } from 'react';

/**
 * @shared:low
 * Reusable hook that uploads a file to a decoupled_toolbox file endpoint
 * and returns the resulting file metadata (url, fid, filename, filemime, filesize).
 *
 * Same upload protocol as FileField: POST octet-stream with Content-Disposition.
 * CSRF is handled by the global fetch patch in main.jsx.
 */

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB

export default function useEditorFileUpload(uploadUrl, entityId, { maxSize = MAX_FILE_SIZE } = {}) {
    const uploadUrlRef = useRef(uploadUrl);
    uploadUrlRef.current = uploadUrl;
    const entityIdRef = useRef(entityId);
    entityIdRef.current = entityId;

    const uploadFile = useCallback(async (file) => {
        const url = uploadUrlRef.current;
        if (!url) throw new Error('uploadUrl is not configured.');

        if (maxSize && file.size > maxSize) {
            const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
            const limitMB = (maxSize / (1024 * 1024)).toFixed(0);
            throw new Error(`File too large (${sizeMB} MB). Limit: ${limitMB} MB.`);
        }

        const eid = entityIdRef.current;
        const effectiveUrl = eid
            ? `${url}${url.includes('?') ? '&' : '?'}entity_id=${encodeURIComponent(eid)}`
            : url;

        const response = await fetch(effectiveUrl, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/octet-stream',
                'Content-Disposition': `file; filename="${encodeURIComponent(file.name)}"`,
            },
            body: file,
        });

        let json;
        try {
            json = await response.json();
        } catch {
            throw new Error(`Unexpected response (HTTP ${response.status})`);
        }

        if (!response.ok || json?.status?.type === 'error') {
            throw new Error(json?.status?.message || `Upload failed (HTTP ${response.status})`);
        }

        return {
            url: json?.data?.url,
            fid: json?.data?.fid,
            filename: json?.data?.filename,
            filemime: json?.data?.filemime,
            filesize: json?.data?.filesize,
            // delete_url is a TOP-LEVEL field of the upload response (sibling of
            // .data), used to remove an orphan temporary file on cancel.
            deleteUrl: json?.delete_url,
        };
    }, [maxSize]);

    return { uploadFile };
}
